// Third-party packages
import {
	TypeConfig,
} from '@modules/config';
import {
	createSlice,
	PayloadAction,
} from '@reduxjs/toolkit';

type DisplayType = 'desktop' | 'mobile' | 'tablet';

const allowedDisplays: DisplayType[] = [
	'desktop',
	'mobile',
	'tablet',
];

export interface AppDeviceState {
	navIsOpen: boolean;
}

export interface AppLocalizationState {
	actualLanguage: string;
	defaultLanguage: string;
}

export interface AppRoutesState {
	[key: string]: string;
	from: string;
}

export interface AppState {
	config: TypeConfig;
	dataLayer: object[];
	desktop: AppDeviceState;
	isLoading: boolean;
	localization: AppLocalizationState;
	mobile: AppDeviceState;
	tablet: AppDeviceState;
	routes: AppRoutesState;
}

const initialState: AppState = {
	config: {},
	dataLayer: [
		{},
	],
	desktop: {
		navIsOpen: false,
	},
	isLoading: true,
	localization: {
		actualLanguage: 'fr-FR',
		defaultLanguage: 'fr-FR',
	},
	mobile: {
		navIsOpen: false,
	},
	routes: {
		from: null as unknown as string,
	},
	tablet: {
		navIsOpen: false,
	},
};

export const appSlice = createSlice({
	initialState,
	name: 'app',
	reducers: {
		appNavClose: (state, action: PayloadAction<DisplayType>) => {
			if (allowedDisplays.includes(action.payload)) {
				const display = action.payload as 'desktop' | 'tablet' | 'mobile';
				if (!state[display]) {
					(state as any)[display] = {
						navIsOpen: false,
					};
				}
				state[display] = {
					...state[action.payload],
					navIsOpen: false,
				};
			}
		},
		appNavOpen: (state, action: PayloadAction<DisplayType>) => {
			if (allowedDisplays.includes(action.payload)) {
				const display = action.payload as 'desktop' | 'tablet' | 'mobile';
				if (!state[display]) {
					(state as any)[display] = {
						navIsOpen: false,
					};
				}
				state[display] = {
					...state[action.payload],
					navIsOpen: true,
				};
			}
		},
		appNavSet: (state, action: PayloadAction<DisplayType>) => {
			if (allowedDisplays.includes(action.payload)) {
				const display = action.payload as 'desktop' | 'tablet' | 'mobile';
				if (!state[display]) {
					state[display] = {
						navIsOpen: false,
					};
				}
			}
		},
		appNavToggle: (state, action: PayloadAction<DisplayType>) => {
			if (allowedDisplays.includes(action.payload)) {
				const display = action.payload as 'desktop' | 'tablet' | 'mobile';
				if (!state[display]) {
					(state as any)[display] = {
						navIsOpen: false,
					};
				}
				state[display] = {
					...state[action.payload],
					navIsOpen: !state[display].navIsOpen,
				};
			}
		},
		appSetConfig: (state, action: PayloadAction<TypeConfig>) => {
			state.config = action.payload;
		},
		appSetRoute: (state, action) => {
			state.routes = {
				...state.routes,
				[action.payload.name]: action.payload.url,
			};
		},
		appSwitchLanguage: (state, action) => {
			state.localization = {
				...state.localization,
				actualLanguage: action.payload,
			};
		},
	},
});

export const {
	appNavClose,
	appNavOpen,
	appNavSet,
	appNavToggle,
	appSetConfig,
	appSetRoute,
	appSwitchLanguage,
} = appSlice.actions;

export const appReducer = appSlice.reducer;
